import { httpsCallable } from "firebase/functions";
import { doc, onSnapshot } from "firebase/firestore";
import { useCallback, useEffect, useRef, useState } from "react";
import { agents, initialAgentRuns, inventory, recommendations, stations, workers } from "../data/demo";
import { auth, db, functions } from "../firebase";
import type {
  AgentRun,
  DelayAlert,
  LiveSnapshot,
  Recommendation,
  WorkerId,
} from "../types";

type FocusMode = "idle" | "offered" | "active";

const initialSnapshot: LiveSnapshot = {
  clock: "18:35:00",
  orders: 7,
  averageEta: "08:12",
  synchronized: 86,
  workers,
  stations,
  inventory,
  activeRecommendation: null,
  agentRuns: initialAgentRuns,
  delayAlert: null,
};

function readDelayAlert(data: Record<string, unknown>): DelayAlert | null {
  const status = data.status === "active" ? "active" : data.status === "clear" ? "clear" : null;
  if (
    !status ||
    typeof data.itemLabel !== "string" ||
    typeof data.stationLabel !== "string" ||
    typeof data.predictedReadyInMinutes !== "number" ||
    typeof data.predictedDelayMinutes !== "number" ||
    typeof data.similarity !== "number" ||
    typeof data.summary !== "string" ||
    typeof data.evidenceCount !== "number"
  ) {
    return null;
  }
  return {
    itemLabel: data.itemLabel,
    stationLabel: data.stationLabel,
    predictedReadyInMinutes: data.predictedReadyInMinutes,
    predictedDelayMinutes: data.predictedDelayMinutes,
    similarity: data.similarity,
    summary: data.summary,
    evidenceCount: data.evidenceCount,
    status,
    updatedAt: data.updatedAt,
  };
}

function nowClock() {
  return new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date());
}

function withRun(snapshot: LiveSnapshot, run: Omit<AgentRun, "id" | "time">) {
  const next: AgentRun = {
    ...run,
    id: `run-${Date.now()}`,
    time: nowClock(),
  };
  return { ...snapshot, agentRuns: [next, ...snapshot.agentRuns].slice(0, 12) };
}

export function useKitchenRuntime() {
  const [snapshot, setSnapshot] = useState<LiveSnapshot>(initialSnapshot);
  const [mode, setMode] = useState<FocusMode>("idle");
  const [connected, setConnected] = useState(false);
  const [gestureHint, setGestureHint] = useState("Double-press to request an action");
  const recommendationIndex = useRef(0);
  const tapTimer = useRef<number | undefined>(undefined);
  const lastTapAt = useRef(0);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setSnapshot((current) => ({ ...current, clock: nowClock() }));
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let unsubscribeFocus: () => void = () => undefined;
    let unsubscribeDelayAlert: () => void = () => undefined;
    void Promise.resolve()
      .then(async () => {
        if (!auth.currentUser) throw new Error("Authentication required");
        const joinDemo = httpsCallable(functions, "joinDemo");
        await joinDemo({ restaurantId: "demo-chinese-kitchen" });
        setConnected(true);
        unsubscribeFocus = onSnapshot(
          doc(db, "restaurants", "demo-chinese-kitchen", "live", "uiFocus"),
          (focusSnapshot) => {
            const data = focusSnapshot.data();
            if (!data?.recommendationId) return;
            const recommendation = recommendations.find(
              (item) => item.id === data.recommendationId,
            );
            if (!recommendation) return;
            recommendationIndex.current = recommendations.indexOf(recommendation);
            setSnapshot((current) => ({
              ...current,
              activeRecommendation: {
                ...recommendation,
                status: data.status ?? "proposed",
              },
            }));
            setMode(data.status === "approved" ? "active" : "offered");
          },
        );
        unsubscribeDelayAlert = onSnapshot(
          doc(db, "restaurants", "demo-chinese-kitchen", "live", "delayAlert"),
          (alertSnapshot) => {
            if (!alertSnapshot.exists()) return;
            const alert = readDelayAlert(alertSnapshot.data());
            if (!alert) return;
            setSnapshot((current) => ({ ...current, delayAlert: alert }));
          },
        );
      })
      .catch(() => {
        setConnected(false);
      });
    return () => {
      unsubscribeFocus();
      unsubscribeDelayAlert();
    };
  }, []);

  const sendGesture = useCallback(
    (gesture: "single_press" | "double_press", workerId: WorkerId) => {
      const handleWristGesture = httpsCallable(functions, "handleWristGesture");
      void handleWristGesture({
        restaurantId: "demo-chinese-kitchen",
        workerId,
        gesture,
        deviceEventId: `web-${Date.now()}-${gesture}`,
        occurredAt: new Date().toISOString(),
      }).catch(() => undefined);
    },
    [],
  );

  const offerRecommendation = useCallback(
    (index = 0) => {
      recommendationIndex.current = index % recommendations.length;
      const recommendation = {
        ...recommendations[recommendationIndex.current],
        status: "proposed" as const,
      };
      setMode("offered");
      setGestureHint("Single press to accept - double press to pass");
      setSnapshot((current) =>
        withRun(
          {
            ...current,
            activeRecommendation: recommendation,
          },
          {
            agent: "Guild - Coordinator",
            summary: `Offered the ${recommendation.stationId} action to Worker ${recommendation.workerId}`,
            status: "done",
            evidence: recommendation.evidence,
          },
        ),
      );
      sendGesture("double_press", recommendation.workerId);
    },
    [sendGesture],
  );

  const approve = useCallback(() => {
    setMode("active");
    setGestureHint("Task in progress - double-press to refocus");
    setSnapshot((current) => {
      if (!current.activeRecommendation) return current;
      const recommendation: Recommendation = {
        ...current.activeRecommendation,
        status: "approved",
      };
      const workersNext = current.workers.map((worker) =>
        worker.id === recommendation.workerId
          ? {
              ...worker,
              status: "moving" as const,
              targetPosition: current.stations.find(
                (station) => station.id === recommendation.stationId,
              )?.position ?? worker.targetPosition,
            }
          : worker,
      );
      return withRun(
        {
          ...current,
          workers: workersNext,
          activeRecommendation: recommendation,
        },
        {
          agent: "Guild - Coordinator",
          summary: `Worker ${recommendation.workerId} accepted - route and task activated`,
          status: "done",
        },
      );
    });
    sendGesture(
      "single_press",
      recommendations[recommendationIndex.current].workerId,
    );
  }, [sendGesture]);

  const pass = useCallback(() => {
    const previous = recommendations[recommendationIndex.current];
    const nextIndex = (recommendationIndex.current + 1) % recommendations.length;
    recommendationIndex.current = nextIndex;
    const alternate = {
      ...recommendations[nextIndex],
      status: "proposed" as const,
    };
    setMode("offered");
    setGestureHint("Alternative ready - single press to accept");
    setSnapshot((current) =>
      withRun(
        {
          ...current,
          activeRecommendation: alternate,
        },
        {
          agent: "Pioneer - Reasoning",
          summary: `${previous.title} passed - ranked a new action with the updated constraint`,
          status: "done",
          evidence: alternate.evidence,
        },
      ),
    );
    sendGesture("double_press", previous.workerId);
  }, [sendGesture]);

  const registerBandPress = useCallback(() => {
    const currentTime = performance.now();
    const withinDoubleWindow = currentTime - lastTapAt.current < 420;
    lastTapAt.current = currentTime;

    if (withinDoubleWindow && tapTimer.current) {
      window.clearTimeout(tapTimer.current);
      tapTimer.current = undefined;
      if (mode === "offered") pass();
      else if (mode === "idle") offerRecommendation(0);
      else setGestureHint("Current action refocused");
      return;
    }

    setGestureHint("Press once more to complete a double press");
    tapTimer.current = window.setTimeout(() => {
      tapTimer.current = undefined;
      if (mode === "offered") approve();
      else if (mode === "idle") setGestureHint("Two consecutive presses are required");
      else setGestureHint("Task in progress");
    }, 420);
  }, [approve, mode, offerRecommendation, pass]);

  const reset = useCallback(() => {
    recommendationIndex.current = 0;
    setMode("idle");
    setGestureHint("Double-press to request an action");
    setSnapshot(initialSnapshot);
  }, []);

  return {
    agents,
    approve,
    connected,
    gestureHint,
    mode,
    offerRecommendation,
    pass,
    registerBandPress,
    reset,
    snapshot,
  };
}
